import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

// English translations
const en = {
  // Header
  nav: {
    gallery: 'Gallery',
    about: 'About',
    location: 'Location',
    attractions: 'Attractions',
    faq: 'FAQ',
    contact: 'Contact',
    reserve: 'Reserve',
  },
  // Hero
  hero: {
    welcome: 'Welcome to the heart of Sarajevo',
    tagline: 'Stay in the heart of Sarajevo, near the Presidency, with parking and complimentary transfer from and to Sarajevo International Airport.',
    cta: 'Send inquiry',
    parkView: 'Park view',
    freeWifi: 'Free WiFi',
    climate: 'AC & Heating',
    rating: '10.0/10 on Booking.com',
    reviews: '29 verified reviews',
    parking: 'Private parking',
    airportTransfer: 'Airport pickup & drop-off',
    size: '60 m²',
    centralLocation: 'Central Sarajevo',
  },
  // Gallery
  gallery: {
    title: 'Gallery',
    subtitle: 'Apartment photos',
    description: 'Take a look at our modern apartment in central Sarajevo',
  },
  // About
  about: {
    title: 'About the Apartment',
    subtitle: 'About us',
    description: 'Brand new apartment with new equipment and furniture in the heart of Sarajevo. Guests can enjoy a modern interior and all amenities for a comfortable stay.',
    fullDescription: 'The apartment features air conditioning, a private bathroom with free toiletries, and a fully equipped kitchen. Additional amenities include a spacious living room with Smart TV, Smart TV in the bedroom, heating, and free WiFi. Private parking is also available.',
    capacity: 'Capacity',
    capacityValue: '2 adults + child',
    checkIn: 'Check-in',
    checkInValue: 'From 2:00 PM',
    checkOut: 'Check-out',
    checkOutValue: 'Until 10:00 AM',
  },
  // Amenities
  amenities: {
    wifi: 'Free WiFi',
    ac: 'Air conditioning',
    heating: 'Heating',
    parking: 'Parking',
    tv: 'Smart TV',
    parkView: 'Park view',
    airportTransfer: 'Airport transfer',
  },
  // Location
  location: {
    title: 'Location',
    subtitle: 'Where we are',
    description: 'Perfectly positioned in the heart of Sarajevo, near the Presidency building',
    directions: 'Get directions',
    address: 'Koševo 21, 71000 Sarajevo, Bosnia and Herzegovina',
  },
  // Attractions
  attractions: {
    title: 'What to Visit?',
    subtitle: 'Explore Sarajevo',
    description: 'Discover the most beautiful places in and around Sarajevo',
    distance: 'Distance',
    items: {
      bascarsija: {
        name: 'Baščaršija',
        description: "The heart of Sarajevo - a historic bazaar with authentic craft shops, traditional cafes, and the Sebilj fountain. Stroll through cobblestone streets, try ćevapi and Bosnian coffee, and enjoy the unique atmosphere of the Old Town.",
        distance: '15 min walk',
      },
      interfaith: {
        name: 'Crossroads of Faiths and Cultures',
        description: 'Sarajevo has been known for centuries as a meeting place of religions, where churches, mosques, and synagogues coexist in a small area. Throughout its history, Catholic, Islamic, Orthodox, and Jewish traditions intertwine, creating a unique cultural and spiritual mosaic.',
        distance: '2 min walk',
      },
      vijecnica: {
        name: 'City Hall (Vijećnica)',
        description: 'A symbolic building of Sarajevo in pseudo-Moorish style. Renovated after the war, it is now a cultural center and host to numerous exhibitions and concerts. It is especially impressive at night thanks to its illumination.',
        distance: '12 min walk',
      },
      trebevic: {
        name: 'Trebević',
        description: 'The mountain overlooking Sarajevo with the Olympic bobsled track. The cable car takes you to the top in just 10 minutes with a beautiful view of the city. Ideal for hiking, zip-lining, and enjoying nature.',
        distance: '20 min walk to cable car',
      },
      vrelobosne: {
        name: 'Vrelo Bosne',
        description: "The source of the Bosna River surrounded by a beautiful park - a perfect escape from the city bustle. A walk through the plane tree avenue, a horse-drawn carriage ride, and enjoying the crystal-clear water will leave you breathless.",
        distance: '20 min by car',
      },
      latinbridge: {
        name: 'Latin Bridge',
        description: 'A historic bridge on the Miljacka River, known as the site of the assassination of Franz Ferdinand in 1914. A museum dedicated to this event is located nearby.',
        distance: '10 min walk',
      },
      nature: {
        name: 'Between Landmarks and Natural Beauty',
        description: "Sarajevo offers a perfect blend of urban architecture and untouched nature. From the imposing City Hall and historic Latin Bridge to crystal-clear springs and spectacular waterfalls - everything is within reach.",
        distance: 'Within 30 min',
      },
    },
  },
  // FAQ
  faq: {
    title: 'Frequently Asked Questions',
    subtitle: 'FAQ',
    items: [
      {
        question: 'What are the check-in and check-out times?',
        answer: 'Check-in is available from 2:00 PM, and check-out is until 10:00 AM. If you need to adjust the time, please contact us.',
      },
      {
        question: 'Can I bring a pet?',
        answer: 'Unfortunately, pets are not allowed in the apartment.',
      },
      {
        question: 'Does the apartment have parking?',
        answer: 'Yes, the apartment offers free private parking.',
      },
      {
        question: 'Is smoking allowed?',
        answer: 'Smoking is not allowed inside the apartment. A designated smoking area is available in front of the building.',
      },
      {
        question: 'What is the apartment capacity?',
        answer: 'The apartment can accommodate up to 2 adults + a child.',
      },
      {
        question: 'Is airport transfer available?',
        answer: 'Yes, we offer complimentary pickup and drop-off from Sarajevo International Airport.',
      },
    ],
  },
  // Contact
  contact: {
    title: 'Contact',
    subtitle: 'Get in touch',
    description: 'We are here to answer all your questions',
    phone: 'Phone',
    email: 'Email',
    address: 'Address',
    whatsapp: 'WhatsApp',
  },
  // Reservation
  reservation: {
    title: 'Send Inquiry',
    subtitle: 'Reservation',
    description: 'Fill out the form and we will get back to you with an offer as soon as possible.',
    checkIn: 'Check-in date',
    checkOut: 'Check-out date',
    selectDate: 'Select date',
    guests: 'Number of guests',
    selectGuests: 'Select number of guests',
    person1: '1 person',
    persons2: '2 persons',
    persons2child: '2 persons + child',
    name: 'Full name',
    namePlaceholder: 'Your full name',
    email: 'Email address',
    emailPlaceholder: 'your@email.com',
    message: 'Message (optional)',
    messagePlaceholder: 'Additional notes or questions...',
    agreement: 'I agree to the processing of personal data for the purpose of processing the inquiry',
    submit: 'Send inquiry',
    sending: 'Sending...',
    success: 'Inquiry sent!',
    successMessage: 'Thank you for your interest. We will get back to you as soon as possible with an offer.',
    newInquiry: 'Send a new inquiry',
    error: 'An error occurred while sending the inquiry. Please try again or contact us directly.',
  },
  // Footer
  footer: {
    quickLinks: 'Quick Links',
    contact: 'Contact',
    rights: 'All rights reserved',
  },
};

// Croatian/Bosnian translations
const hr = {
  // Header
  nav: {
    gallery: 'Galerija',
    about: 'O apartmanu',
    location: 'Lokacija',
    attractions: 'Atrakcije',
    faq: 'FAQ',
    contact: 'Kontakt',
    reserve: 'Rezerviraj',
  },
  // Hero
  hero: {
    welcome: 'Dobrodošli u srce Sarajeva',
    tagline: 'Smještaj u srcu Sarajeva s pogledom na park, privatnim parkingom i besplatnim transferom od i do Međunarodnog aerodroma Sarajevo.',
    cta: 'Pošalji upit',
    parkView: 'Pogled na park',
    freeWifi: 'Besplatan WiFi',
    climate: 'Klima & grijanje',
    rating: '10.0/10 na Booking.com',
    reviews: '29 potvrđenih recenzija',
    parking: 'Privatni parking',
    airportTransfer: 'Aerodromski transfer',
    size: '60 m²',
    centralLocation: 'Centar Sarajeva',
  },
  // Gallery
  gallery: {
    title: 'Galerija',
    subtitle: 'Fotografije apartmana',
    description: 'Pogledajte naš moderni apartman u centru Sarajeva',
  },
  // About
  about: {
    title: 'O apartmanu',
    subtitle: 'O nama',
    description: 'Potpuno nov apartman s novom opremom i namještajem u samom centru Sarajeva. Gosti mogu uživati u modernom interijeru i svim pogodnostima za ugodan boravak.',
    fullDescription: 'Apartman ima klimu, privatno kupatilo s besplatnim toaletnim potrepštinama i potpuno opremljenu kuhinju. Dodatne pogodnosti uključuju veliku dnevnu sobu sa Smart TV-om, Smart TV u spavaćoj sobi, grijanje i besplatni WiFi. Na raspolaganju je i privatno parkirno mjesto.',
    capacity: 'Kapacitet',
    capacityValue: '2 odrasle osobe + dijete',
    checkIn: 'Prijava',
    checkInValue: 'Od 14:00',
    checkOut: 'Odjava',
    checkOutValue: 'Do 10:00',
  },
  // Amenities
  amenities: {
    wifi: 'Besplatan WiFi',
    ac: 'Klima uređaj',
    heating: 'Grijanje',
    parking: 'Parking',
    tv: 'Smart TV',
    parkView: 'Pogled na park',
    airportTransfer: 'Aerodromski transfer',
  },
  // Location
  location: {
    title: 'Lokacija',
    subtitle: 'Gdje se nalazimo',
    description: 'Savršeno pozicionirani u srcu Sarajeva, u blizini zgrade Predsjedništva',
    directions: 'Upute za dolazak',
    address: 'Koševo 21, 71000 Sarajevo, Bosna i Hercegovina',
  },
  // Attractions
  attractions: {
    title: 'Šta posjetiti?',
    subtitle: 'Istražite Sarajevo',
    description: 'Otkrijte najljepša mjesta u Sarajevu i okolici',
    distance: 'Udaljenost',
    items: {
      bascarsija: {
        name: 'Baščaršija',
        description: 'Srce Sarajeva - historijski bazar s autentičnim zanatskim radnjama, tradicionalnim kafama i Sebiljem. Prošetajte kaldrmanim ulicama, probajte ćevape i bosansku kafu, te uživajte u jedinstvenoj atmosferi Starog grada.',
        distance: '15 min hoda',
      },
      interfaith: {
        name: 'Raskrižje vjera i kultura',
        description: 'Sarajevo je stoljećima poznato kao mjesto susreta religija, gdje se na malom prostoru susreću crkve, džamije i sinagoge. U njegovoj povijesti isprepliću se katolička, islamska, pravoslavna i židovska tradicija, stvarajući jedinstven kulturni i duhovni mozaik.',
        distance: '2 min hoda',
      },
      vijecnica: {
        name: 'Vijećnica',
        description: 'Simbolička građevina Sarajeva u pseudo-maurskom stilu. Obnovljena nakon rata, danas je kulturni centar i domaćin brojnih izložbi i koncerata. Noću je posebno impresivna zahvaljujući rasvjeti.',
        distance: '12 min hoda',
      },
      trebevic: {
        name: 'Trebević',
        description: 'Planina koja nadvisuje Sarajevo s olimpijskom bobsled stazom. Žičarom se za samo 10 minuta popnete na vrh s predivnim pogledom na grad. Idealno za planinarenje, zip-line i uživanje u prirodi.',
        distance: '20 min hoda do žičare',
      },
      vrelobosne: {
        name: 'Vrelo Bosne',
        description: 'Izvor rijeke Bosne okružen prekrasnim parkom - savršeno mjesto za bijeg od gradske vreve. Šetnja alejom platana, vožnja fijakerom i uživanje u kristalno čistoj vodi ostavit će vas bez daha.',
        distance: '20 min autom',
      },
      latinbridge: {
        name: 'Latinska ćuprija',
        description: 'Historijski most na rijeci Miljacki, poznat kao mjesto atentata na Franza Ferdinanda 1914. godine. Muzej posvećen ovom događaju nalazi se u neposrednoj blizini.',
        distance: '10 min hoda',
      },
      nature: {
        name: 'Između znamenitosti i prirodnih ljepota',
        description: 'Sarajevo nudi savršen spoj urbane arhitekture i netaknute prirode. Od impozantne Vijećnice i historijske Latinske ćuprije, do kristalno čistih izvora i spektakularnih vodopada - sve je nadohvat ruke.',
        distance: 'U krugu od 30 min',
      },
    },
  },
  // FAQ
  faq: {
    title: 'Često postavljana pitanja',
    subtitle: 'FAQ',
    items: [
      {
        question: 'Koje je vrijeme prijave i odjave?',
        answer: 'Prijava (check-in) je moguća od 14:00, a odjava (check-out) do 10:00. U slučaju potrebe, molimo kontaktirajte nas za prilagodbu termina.',
      },
      {
        question: 'Da li mogu dovesti kućnog ljubimca?',
        answer: 'Nažalost, kućni ljubimci nisu dozvoljeni u apartmanu.',
      },
      {
        question: 'Da li apartman ima parking?',
        answer: 'Da, apartman nudi besplatno privatno parkirno mjesto.',
      },
      {
        question: 'Da li je dozvoljeno pušenje?',
        answer: 'Pušenje nije dozvoljeno unutar apartmana. Za pušenje je predviđen prostor ispred zgrade.',
      },
      {
        question: 'Koliki je kapacitet apartmana?',
        answer: 'Apartman može primiti do 2 odrasle osobe + dijete.',
      },
      {
        question: 'Da li je dostupan aerodromski transfer?',
        answer: 'Da, nudimo besplatan prijevoz od i do Međunarodnog aerodroma Sarajevo.',
      },
    ],
  },
  // Contact
  contact: {
    title: 'Kontakt',
    subtitle: 'Javite nam se',
    description: 'Tu smo da odgovorimo na sva vaša pitanja',
    phone: 'Telefon',
    email: 'Email',
    address: 'Adresa',
    whatsapp: 'WhatsApp',
  },
  // Reservation
  reservation: {
    title: 'Pošaljite upit',
    subtitle: 'Rezervacija',
    description: 'Ispunite formu i javit ćemo vam se s ponudom u najkraćem roku.',
    checkIn: 'Datum dolaska',
    checkOut: 'Datum odlaska',
    selectDate: 'Odaberite datum',
    guests: 'Broj gostiju',
    selectGuests: 'Odaberite broj gostiju',
    person1: '1 osoba',
    persons2: '2 osobe',
    persons2child: '2 osobe + dijete',
    name: 'Ime i prezime',
    namePlaceholder: 'Vaše ime i prezime',
    email: 'Email adresa',
    emailPlaceholder: 'vasa@email.com',
    message: 'Poruka (opcionalno)',
    messagePlaceholder: 'Dodatne napomene ili pitanja...',
    agreement: 'Slažem se s obradom osobnih podataka u svrhu obrade upita',
    submit: 'Pošalji upit',
    sending: 'Šaljem...',
    success: 'Upit je poslan!',
    successMessage: 'Hvala vam na interesu. Javit ćemo vam se u najkraćem mogućem roku s ponudom.',
    newInquiry: 'Pošaljite novi upit',
    error: 'Došlo je do greške pri slanju upita. Molimo pokušajte ponovo ili nas kontaktirajte direktno.',
  },
  // Footer
  footer: {
    quickLinks: 'Brzi linkovi',
    contact: 'Kontakt',
    rights: 'Sva prava pridržana',
  },
};

const translations = { en, hr };

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Check URL path for language
    if (typeof window !== 'undefined') {
      const path = window.location.pathname;
      if (path.startsWith('/hr')) return 'hr';
    }
    return 'en'; // Default to English for international SEO
  });

  const t = translations[language] || translations.en;

  const switchLanguage = (lang) => {
    setLanguage(lang);
    // Update URL to reflect language change
    const newPath = lang === 'en' ? '/' : '/hr';
    if (typeof window !== 'undefined' && window.location.pathname !== newPath) {
      // Use window.location to trigger full navigation and ensure React Router syncs
      window.location.href = newPath;
    }
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage: switchLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
